/**
 * ProfileUserIdentiteController — onglet « Identité » de
 * /profil-utilisateur.html (EPIC-099 / 99.73 §2f).
 *
 * Pilote 2 nm-edit-block indépendants stockés sur la table `users` :
 *  - `block-etat-civil`    : civilité, prénom, nom de naissance, nom
 *                            d'usage, date / lieu / pays de naissance.
 *  - `block-adresse-perso` : adresse personnelle (ligne, CP, ville, pays).
 *
 * Comble un gap d'EPIC-007 / 7.7 où ces boutons « Modifier » étaient
 * `disabled` faute d'endpoints. Distinct du Profil entreprise (identité
 * société).
 *
 * Pré-remplissage : `GET /me/profile/identity` renvoie un flag
 * `source`. Quand `source === 'inpi_draft'`, les valeurs proviennent de la
 * formalité INPI de création et sont présentées en suggestion éditable
 * (badge « Suggestion ») tant que le ME n'a pas enregistré.
 *
 * Convention CLAUDE.md : pas d'HTML inline en JS (résumés rendus depuis
 * <template> via cloneTemplate / fillSlots).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const CIVILITY_LABELS = { madame: "Madame", monsieur: "Monsieur" };

export default class ProfileUserIdentiteController extends BaseController {
  static targets = [
    "blockEtatCivil",
    "blockAdressePerso",
    "etatCivilSummary",
    "adressePersoSummary",
    "civilityInput",
    "firstNameInput",
    "birthLastNameInput",
    "usageNameInput",
    "birthDateInput",
    "birthPlaceInput",
    "birthCountryInput",
    "addressLine1Input",
    "addressPostalCodeInput",
    "addressCityInput",
    "addressCountryInput",
    "etatCivilSubmit",
    "adressePersoSubmit",
    "etatCivilError",
    "adressePersoError",
  ];

  state = {
    source: "empty",
    civilStatus: {
      civility: null,
      firstName: null,
      birthLastName: null,
      usageName: null,
      birthDate: null,
      birthPlace: null,
      birthCountry: null,
    },
    address: {
      line1: null,
      postalCode: null,
      city: null,
      country: null,
    },
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    await this._refreshFromApi();
  }

  async _refreshFromApi() {
    try {
      const data = await api.getIdentity();
      if (!data) return;
      this.state.source = data.source ?? "empty";
      if (data.civilStatus) this._hydrateCivilStatus(data.civilStatus);
      if (data.address) this._hydrateAddress(data.address);
    } catch (error) {
      // Hydratation silencieuse — l'utilisateur peut toujours saisir.
      console.warn("[profile-user-identite] hydration failed", error);
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openEtatCivil(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreEtatCivilInputs();
    this._hideError("etatCivil");
    this._open(block);
  }

  openAdressePerso(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreAdressePersoInputs();
    this._hideError("adressePerso");
    this._open(block);
  }

  cancelEtatCivil(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreEtatCivilInputs();
    this._hideError("etatCivil");
    this._close(block);
  }

  cancelAdressePerso(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreAdressePersoInputs();
    this._hideError("adressePerso");
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submitEtatCivil(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = event.target;
    if (form && typeof form.checkValidity === "function" && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const firstName = this._readTrim("firstNameInput");
    const birthLastName = this._readTrim("birthLastNameInput");
    const birthDate = this._readTrim("birthDateInput");
    if (firstName === "" || birthLastName === "" || birthDate === "") {
      this._showError("etatCivil", "Prénom, nom de naissance et date de naissance sont obligatoires.");
      return;
    }

    const payload = {
      civility: this._readNullable("civilityInput"),
      firstName,
      birthLastName,
      usageName: this._readNullable("usageNameInput"),
      birthDate,
      birthPlace: this._readNullable("birthPlaceInput"),
      birthCountry: this._readNullable("birthCountryInput"),
    };

    if (this.hasEtatCivilSubmitTarget) this.etatCivilSubmitTarget.disabled = true;
    try {
      await api.updateCivilStatus(payload);
      await this._refreshFromApi();
      if (this.hasBlockEtatCivilTarget) this._close(this.blockEtatCivilTarget);
      this.showSuccess("État civil enregistré.");
    } catch (error) {
      this._showError("etatCivil", this._extractMessage(error, "Erreur lors de l'enregistrement de l'état civil."));
    } finally {
      if (this.hasEtatCivilSubmitTarget) this.etatCivilSubmitTarget.disabled = false;
    }
  }

  async submitAdressePerso(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = event.target;
    if (form && typeof form.checkValidity === "function" && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const line1 = this._readTrim("addressLine1Input");
    const postalCode = this._readTrim("addressPostalCodeInput");
    const city = this._readTrim("addressCityInput");
    if (line1 === "" || postalCode === "" || city === "") {
      this._showError("adressePerso", "Adresse, code postal et ville sont obligatoires.");
      return;
    }

    const payload = {
      line1,
      postalCode,
      city,
      country: this._readNullable("addressCountryInput"),
    };

    if (this.hasAdressePersoSubmitTarget) this.adressePersoSubmitTarget.disabled = true;
    try {
      await api.updatePersonalAddress(payload);
      await this._refreshFromApi();
      if (this.hasBlockAdressePersoTarget) this._close(this.blockAdressePersoTarget);
      this.showSuccess("Adresse personnelle enregistrée.");
    } catch (error) {
      this._showError("adressePerso", this._extractMessage(error, "Erreur lors de l'enregistrement de l'adresse."));
    } finally {
      if (this.hasAdressePersoSubmitTarget) this.adressePersoSubmitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration + render
  // ---------------------------------------------------------------------------

  _hydrateCivilStatus(civilStatus) {
    this.state.civilStatus = {
      civility: civilStatus.civility ?? null,
      firstName: civilStatus.firstName ?? null,
      birthLastName: civilStatus.birthLastName ?? null,
      usageName: civilStatus.usageName ?? null,
      birthDate: civilStatus.birthDate ?? null,
      birthPlace: civilStatus.birthPlace ?? null,
      birthCountry: civilStatus.birthCountry ?? null,
    };
    this._renderEtatCivilSummary();
    this._restoreEtatCivilInputs();
  }

  _hydrateAddress(address) {
    this.state.address = {
      line1: address.line1 ?? null,
      postalCode: address.postalCode ?? null,
      city: address.city ?? null,
      country: address.country ?? null,
    };
    this._renderAdressePersoSummary();
    this._restoreAdressePersoInputs();
  }

  _renderEtatCivilSummary() {
    if (!this.hasEtatCivilSummaryTarget) return;
    const c = this.state.civilStatus;
    const isEmpty = !c.firstName && !c.birthLastName && !c.birthDate;
    const fragment = this.cloneTemplate(
      isEmpty ? "tpl-etat-civil-summary-empty" : "tpl-etat-civil-summary-filled",
    );
    if (!isEmpty) {
      const name = [c.firstName, c.usageName ? c.usageName : c.birthLastName]
        .filter(Boolean)
        .join(" ");
      this.fillSlots(fragment, {
        civility: c.civility ? (CIVILITY_LABELS[c.civility] ?? "") : "",
        name: name || "—",
        birthDate: c.birthDate ? this._formatDate(c.birthDate) : "—",
        birthPlace: c.birthPlace ?? "—",
      });
      this._toggleChunk(fragment, "birth", Boolean(c.birthDate));
      this._toggleChunk(fragment, "birthPlace", Boolean(c.birthPlace));
      this._toggleChunk(fragment, "suggestion", this.state.source === "inpi_draft");
    }
    this._replaceChildren(this.etatCivilSummaryTarget, fragment);
  }

  _renderAdressePersoSummary() {
    if (!this.hasAdressePersoSummaryTarget) return;
    const a = this.state.address;
    const isEmpty = !a.line1 && !a.postalCode && !a.city;
    const fragment = this.cloneTemplate(
      isEmpty ? "tpl-adresse-perso-summary-empty" : "tpl-adresse-perso-summary-filled",
    );
    if (!isEmpty) {
      const parts = [a.line1, [a.postalCode, a.city].filter(Boolean).join(" "), a.country]
        .filter((v) => v && v.trim() !== "");
      this.fillSlots(fragment, {
        address: parts.length > 0 ? parts.join(" · ") : "—",
      });
      this._toggleChunk(fragment, "suggestion", this.state.source === "inpi_draft");
    }
    this._replaceChildren(this.adressePersoSummaryTarget, fragment);
  }

  _restoreEtatCivilInputs() {
    const c = this.state.civilStatus;
    if (this.hasCivilityInputTarget) this.civilityInputTarget.value = c.civility ?? "";
    if (this.hasFirstNameInputTarget) this.firstNameInputTarget.value = c.firstName ?? "";
    if (this.hasBirthLastNameInputTarget) this.birthLastNameInputTarget.value = c.birthLastName ?? "";
    if (this.hasUsageNameInputTarget) this.usageNameInputTarget.value = c.usageName ?? "";
    if (this.hasBirthDateInputTarget) this.birthDateInputTarget.value = c.birthDate ?? "";
    if (this.hasBirthPlaceInputTarget) this.birthPlaceInputTarget.value = c.birthPlace ?? "";
    if (this.hasBirthCountryInputTarget) this.birthCountryInputTarget.value = c.birthCountry ?? "";
  }

  _restoreAdressePersoInputs() {
    const a = this.state.address;
    if (this.hasAddressLine1InputTarget) this.addressLine1InputTarget.value = a.line1 ?? "";
    if (this.hasAddressPostalCodeInputTarget) this.addressPostalCodeInputTarget.value = a.postalCode ?? "";
    if (this.hasAddressCityInputTarget) this.addressCityInputTarget.value = a.city ?? "";
    if (this.hasAddressCountryInputTarget) this.addressCountryInputTarget.value = a.country ?? "";
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatDate(iso) {
    // iso = "YYYY-MM-DD" → "DD/MM/YYYY" sans construire de Date (évite les
    // décalages de fuseau sur une date civile).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  }

  _toggleChunk(fragment, name, visible) {
    const chunk = fragment.querySelector(`[data-summary-chunk="${name}"]`);
    if (chunk) chunk.hidden = !visible;
  }

  _readTrim(targetName) {
    const has = this[`has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`];
    if (!has) return "";
    const el = this[`${targetName}Target`];
    return (el && typeof el.value === "string") ? el.value.trim() : "";
  }

  _readNullable(targetName) {
    const trimmed = this._readTrim(targetName);
    return trimmed === "" ? null : trimmed;
  }

  _extractMessage(error, fallback) {
    return error && error.message ? error.message : fallback;
  }

  _showError(block, message) {
    const hasTarget = block === "etatCivil" ? this.hasEtatCivilErrorTarget : this.hasAdressePersoErrorTarget;
    if (!hasTarget) {
      this.showError(message);
      return;
    }
    const el = block === "etatCivil" ? this.etatCivilErrorTarget : this.adressePersoErrorTarget;
    el.textContent = message;
    el.hidden = false;
  }

  _hideError(block) {
    const hasTarget = block === "etatCivil" ? this.hasEtatCivilErrorTarget : this.hasAdressePersoErrorTarget;
    if (!hasTarget) return;
    const el = block === "etatCivil" ? this.etatCivilErrorTarget : this.adressePersoErrorTarget;
    el.textContent = "";
    el.hidden = true;
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
